import type { RadioValue, Fields } from 'types/builder/interface';
import type { SingleValue, MultiValue } from 'react-select';
import type { ReactFCC } from 'types/react';

import { hideOrShowEvents, hideOrShowStores } from 'stores/builder/hideOrShowItems';
import { EditorLocationEnum, FieldTypesEnum } from 'constants/builder/enums';
import { type SelectValue, Select } from 'ui-kit/components/Select/Select';
import { useCallback, useEffect, useMemo } from 'react';
import { Input } from 'ui-kit/components/inputs/Input';
import { editorStores } from 'stores/builder/editor';
import { useUnit } from 'effector-react';

import { MultiselectContainer, SelectContainer, Container, Content, Title, Item, Hr } from './styles';
import { getDefaultShowOrHideString } from './utils';
import { selectOptions } from './constants';
import { Answer } from './Answer';

interface Props {
    formValues?: { [key: string]: string };
    variationItems: RadioValue[];
    selectedValue: string;
    blockId?: string;
    clause?: Fields;
}

export const Logic: ReactFCC<Props> = ({ variationItems, selectedValue, formValues, clause }) => {
    const {
        initializeMultiSelectValues,
        initializeClauseOrBlock,
        setMultiSelectValues,
        initializeShowOrHide,
        setShowOrHide
    } = hideOrShowEvents;
    const showOrHide = useUnit(hideOrShowStores.showOrHide);
    const location = useUnit(editorStores.location);

    const isAddingClauseVariations = useMemo(() => location === EditorLocationEnum.Clause, [location]);

    const handleSelectShowOrHide = (value: string, key: string) => {
        setShowOrHide({ value, key });
    };

    const handleSelectIndex = (
        key: string,
        value: MultiValue<{
            value: string;
            label: string;
        }>
    ) => {
        setMultiSelectValues({ value, key });
    };

    const clauseOptions = useMemo(
        () =>
            clause?.content.map((item, index) => ({
                label: item.isDefault ? 'Default' : `Alternative ${index}`,
                value: index.toString()
            })) || [],
        [clause]
    );

    const initialize = useCallback(() => {
        initializeShowOrHide(
            variationItems.map((item, index) => ({
                value: !isAddingClauseVariations
                    ? item.value.value || (selectedValue === FieldTypesEnum.Radio && index === 0 ? 'show' : 'hide')
                    : item.value.value || 'show',
                key: `radio_${index + 1}_text`
            }))
        );
        initializeClauseOrBlock(
            variationItems.map((_, index) => ({
                key: `radio_${index + 1}_text`,
                value: 'clause'
            }))
        );
        initializeMultiSelectValues(
            variationItems.map(({ value }, index) => {
                const defaultMultiSelectValues =
                    clauseOptions.filter(option =>
                        value.value
                            .split('-')[0]
                            .split(',')
                            .some(index => option.value === String(index))
                    ) || [];

                return {
                    value: defaultMultiSelectValues,
                    key: `radio_${index + 1}_text`
                };
            })
        );
    }, [
        clauseOptions,
        initializeClauseOrBlock,
        initializeMultiSelectValues,
        initializeShowOrHide,
        isAddingClauseVariations,
        selectedValue,
        variationItems
    ]);

    useEffect(() => {
        initialize();
    }, [initialize]);

    return (
        <Container>
            <Hr />
            {showOrHide.map((item, index) => {
                const showOrHideDefaultValue = getDefaultShowOrHideString(item.value, isAddingClauseVariations);

                const defaultMultiSelectValues = clauseOptions.filter(option =>
                    item.value
                        .split('-')[0]
                        .split(',')
                        .some(index => option.value === String(index))
                );

                return (
                    <Content key={index}>
                        <Item>
                            <Title>
                                <b>IF</b> the answer to:
                            </Title>
                            <Answer text={formValues?.value} />
                        </Item>
                        <Item>
                            <Title>IS:</Title>
                            <Answer text={formValues?.[`radio_${index + 1}_text`]} />
                            <Title>THEN:</Title>
                            <SelectContainer>
                                <Select
                                    onChange={value => {
                                        const newValue = value as SingleValue<SelectValue>;
                                        handleSelectShowOrHide(newValue?.label || '', `radio_${index + 1}_text`);
                                    }}
                                    defaultValue={showOrHideDefaultValue}
                                    options={selectOptions}
                                />
                            </SelectContainer>
                            {isAddingClauseVariations ? (
                                <Input value="section" type="text" disabled />
                            ) : (
                                <Input value="article" type="text" disabled />
                            )}
                            {isAddingClauseVariations && (
                                <MultiselectContainer key={defaultMultiSelectValues.toString()}>
                                    <Select
                                        onChange={value => {
                                            const newValue = value as MultiValue<SelectValue>;
                                            handleSelectIndex(`radio_${index + 1}_text`, newValue);
                                        }}
                                        defaultValue={defaultMultiSelectValues}
                                        options={clauseOptions}
                                        isClearable={false}
                                        width="172px"
                                        isMulti
                                    />
                                </MultiselectContainer>
                            )}
                        </Item>
                    </Content>
                );
            })}
        </Container>
    );
};
